import {Checkbox} from "primereact/checkbox";
import {useTranslation} from "react-i18next";

import type {ArchiveDocumentType} from "../../api/payload-types/persistence-finalize";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {
    PERMISSION_RULE_DOC_FULL_BELWERTV24,
    PERMISSION_RULE_DOC_FULL_PFANDBG16,
    PERMISSION_RULE_DOC_LIMITED_SCOPE,
} from "../../util/permissions";

/** One selectable document type: docPath (API value) + i18n label key + PDF fallback-filename base. */
export interface ArchiveDocumentOption {
    docPath: ArchiveDocumentType;
    labelKey: string;
    nameKey: string;
}

/**
 * Document types the current user is allowed to request, in display order
 * (Gutachten, Vereinfachte Wertermittlung § 24 BelWertV – ausführlich bzw. – kompakt). Single source for
 * the permission→docPath mapping — also consumed by ValueToolbar.jsx for the
 * snapshot entries and the PDF preview menu.
 */
export function useAllowedArchiveDocumentTypes(): ArchiveDocumentOption[] {
    const canRequestDocFullPfandbg = useCheckPermission(PERMISSION_RULE_DOC_FULL_PFANDBG16);
    const canRequestDocFullBelwertv = useCheckPermission(PERMISSION_RULE_DOC_FULL_BELWERTV24);
    const canRequestDocLimitedScope = useCheckPermission(PERMISSION_RULE_DOC_LIMITED_SCOPE);

    const options: ArchiveDocumentOption[] = [];
    if (canRequestDocFullPfandbg) {
        options.push({docPath: "full-valuation", labelKey: "value.actions.snapshotPdfFull", nameKey: "Gutachten"});
    }
    if (canRequestDocFullBelwertv) {
        options.push({
            docPath: "valuation",
            labelKey: "value.actions.snapshotPdfSimplified",
            nameKey: "VereinfachteWertermittlung-24BelWertV-ausfuehrlich",
        });
    }
    if (canRequestDocLimitedScope) {
        options.push({
            docPath: "simple-valuation",
            labelKey: "value.actions.snapshotPdfCompact",
            nameKey: "VereinfachteWertermittlung-24BelWertV-kompakt",
        });
    }
    return options;
}

/**
 * Initial checkbox selection for the dialogs: nothing preselected — unless there is
 * exactly one permitted document type, then that one.
 */
export function defaultDocTypeSelection(options: ArchiveDocumentOption[]): ArchiveDocumentType[] {
    return options.length === 1 ? [options[0].docPath] : [];
}

interface Props {
    /** Document types the user may select (from useAllowedArchiveDocumentTypes()) */
    options: ArchiveDocumentOption[];
    /** Currently selected document types */
    selected: ArchiveDocumentType[];
    /** Setter for the selection */
    setSelected: (selected: ArchiveDocumentType[]) => void;
    /** Whether the checkboxes are disabled (e.g. while submitting) */
    disabled?: boolean;
    /** Prefix for the checkbox input ids, to keep them unique per dialog */
    idPrefix: string;
}

/**
 * Checkbox section for the (pre-)finalize dialogs: asks which documents should be
 * generated and archived right now. An empty selection is valid (no documents).
 */
export default function ArchiveDocumentsSelection({options, selected, setSelected, disabled, idPrefix}: Props) {
    const {t} = useTranslation();

    if (options.length === 0) return null;

    const onToggle = (docPath: ArchiveDocumentType, checked: boolean) => {
        if (checked) {
            setSelected([...selected, docPath]);
        } else {
            setSelected(selected.filter((item) => item !== docPath));
        }
    };

    return (
        <div className="flex flex-col gap-2" role="group" aria-labelledby={`${idPrefix}DocTypesLabel`}>
            <label id={`${idPrefix}DocTypesLabel`}>
                {t(
                    options.length === 1
                        ? "value.actions.archiveDocumentQuestionSingle"
                        : "value.actions.archiveDocumentsQuestion",
                )}
            </label>
            {options.map(({docPath, labelKey}) => (
                <div key={docPath} className="flex items-center">
                    <Checkbox
                        inputId={`${idPrefix}DocType-${docPath}`}
                        checked={selected.includes(docPath)}
                        onChange={(e) => onToggle(docPath, e.checked === true)}
                        disabled={disabled}
                    />
                    <label htmlFor={`${idPrefix}DocType-${docPath}`} className="ml-2">
                        {t(labelKey)}
                    </label>
                </div>
            ))}
        </div>
    );
}
